import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Chip, Tooltip } from '@mui/material';

import { tokens } from '../../theme-next';

/**
 * Badge d'état de l'audit IA (Opus/Sonnet) affiché dans la review GOD.
 *
 * Pourquoi : la carte d'audit complète (AuditBatchVerdict) est masquée sur
 * cette page (SHOW_AUDIT_BATCH_VERDICT = false) — l'audit tourne « en
 * sous-marin » (préflight form-cgia + post-fulfill). Le relecteur GOD doit
 * néanmoins savoir d'un coup d'œil si l'audit IA a bien tourné (et sur quel
 * modèle), s'il n'a jamais été lancé, ou s'il a échoué (ex. crédit Anthropic
 * épuisé). Un verdict d'analyse affiché alors que l'audit IA a échoué serait
 * trompeur (cf. règle « honnêteté des messages »).
 *
 * Source : `auditStatus` du payload review (cgReviewController.deriveAuditStatus),
 * qui applique la chaîne d'adoption préflight complète — plus fiable que le GET
 * /audit-batch isolé. Trois états : ok / error / never_run.
 */

export type AuditStatus =
  | { state: 'never_run' }
  | { state: 'error'; modelUsed?: string | null; errorMessage?: string | null; ranAt?: string | null }
  | { state: 'ok'; verdict?: string | null; modelUsed?: string | null; ranAt?: string | null };

export interface AuditBadgeView {
  label: string;
  tooltip: string;
  tone: 'ok' | 'error' | 'neutral';
}

const VERDICT_LABEL: Record<string, string> = {
  OK: 'dossier prêt',
  A_VERIFIER: 'à vérifier',
  KO: 'bloqué',
};

const formatModel = (model?: string | null): string =>
  model ? model.replace(/^claude-/, 'Claude ') : 'modèle inconnu';

/**
 * Formate une date SQL/ISO en « JJ/MM/AAAA à HH:MM » (heure locale). Renvoie
 * null si absente/illisible — le tooltip omet alors la date plutôt que
 * d'afficher « Invalid Date ».
 */
export const formatAuditDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Dérive l'affichage du badge (label + tooltip + tonalité) depuis l'état
 * d'audit. Pur — testé en isolation (pas de DOM).
 */
export const auditStatusBadgeView = (status: AuditStatus): AuditBadgeView => {
  if (status.state === 'ok') {
    const model = formatModel(status.modelUsed);
    const date = formatAuditDate(status.ranAt);
    const verdict = status.verdict ? VERDICT_LABEL[status.verdict] ?? status.verdict : null;
    const parts = [
      `Audit IA exécuté avec succès sur ${model}`,
      date ? `le ${date}` : null,
      verdict ? `· verdict : ${verdict}` : null,
    ].filter(Boolean);
    return { label: 'Audit OK', tooltip: parts.join(' '), tone: 'ok' };
  }

  if (status.state === 'error') {
    const model = formatModel(status.modelUsed);
    const date = formatAuditDate(status.ranAt);
    const reason = status.errorMessage?.trim() || 'cause inconnue';
    const parts = [
      `Audit IA en échec (${model}${date ? `, le ${date}` : ''}) :`,
      reason,
      '· erreur signalée à Sentry et aux logs PM2.',
    ];
    return { label: 'Audit en échec', tooltip: parts.join(' '), tone: 'error' };
  }

  return {
    label: 'Audit jamais exécuté',
    tooltip:
      "Aucun audit IA n'a tourné pour ce jeu de pièces. L'analyse affichée repose " +
      'uniquement sur le moteur de règles déterministe, sans relecture globale par le modèle.',
    tone: 'neutral',
  };
};

const TONE_STYLE: Record<AuditBadgeView['tone'], { bg: string; fg: string; border: string; icon: React.ReactNode }> = {
  ok: {
    bg: tokens.color.greenLight,
    fg: tokens.color.greenDeep ?? tokens.color.greenDark,
    border: tokens.color.greenBorder,
    icon: <CheckCircleIcon sx={{ fontSize: 15 }} />,
  },
  error: {
    bg: tokens.color.redLight ?? '#FFF5F5',
    fg: tokens.color.red ?? '#EF4444',
    border: tokens.color.redBorder ?? '#FECACA',
    icon: <ErrorOutlineIcon sx={{ fontSize: 15 }} />,
  },
  neutral: {
    bg: tokens.color.surface,
    fg: tokens.color.textMuted,
    border: tokens.color.border,
    icon: <HelpOutlineIcon sx={{ fontSize: 15 }} />,
  },
};

export const AuditStatusBadge = ({ status }: { status: AuditStatus }) => {
  const view = auditStatusBadgeView(status);
  const style = TONE_STYLE[view.tone];
  return (
    <Tooltip title={view.tooltip} arrow placement="top">
      <Chip
        size="small"
        icon={<span style={{ display: 'flex', color: style.fg, marginLeft: 6 }}>{style.icon}</span>}
        label={view.label}
        sx={{
          backgroundColor: style.bg,
          color: style.fg,
          fontWeight: 700,
          fontSize: '11px',
          border: `0.5px solid ${style.border}`,
          '& .MuiChip-label': { paddingLeft: '6px' },
        }}
      />
    </Tooltip>
  );
};
